import React from 'react';
import { ArrowRight, Check, GitBranch, Layers3, Map, ShieldCheck } from 'lucide-react';
import Alt4Layout from '../components/Alt4Layout';
import {
  ClosingCta,
  PrimaryCta,
  Reveal,
  SectionIntro,
  StatusPill,
  TextLink,
} from '../components/Alt4Shared';
import PracticeCommandCenter from '../components/PracticeCommandCenter';
import { MIGRATION_STEPS, OPERATING_LAYERS, WORKFLOW_GROUPS } from '../data/site';

const handoffCards = [
  {
    title: 'One patient, one context',
    body: 'Schedules, charts, coverage, messages, and financial context stay attached to the same operational record.',
  },
  {
    title: 'Work that keeps moving',
    body: 'When one step finishes, the PMS can begin the next authorized step without rebuilding context.',
  },
  {
    title: 'Exceptions arrive with the why',
    body: 'Teams see what happened, what was checked, and the decision that still needs a person.',
  },
];

const lifecycleStages = [
  { label: 'Attract', detail: 'Turn patient interest into a coordinated first response.' },
  { label: 'Schedule', detail: 'Turn demand and openings into a coordinated visit.', workflowIndex: 0 },
  { label: 'Prepare', detail: 'Gather forms, coverage, history, and outstanding context.', workflowIndex: 1 },
  { label: 'Treat', detail: 'Keep the clinical plan and office work in one current record.', workflowIndex: 2 },
  { label: 'Collect', detail: 'Carry completed care through claim and payment follow-through.', workflowIndex: 3 },
  { label: 'Retain', detail: 'Continue the relationship with the right next step already queued.', workflowIndex: 4 },
];

const governanceCards = [
  {
    title: 'Rules set the boundary',
    body: 'Define what can run automatically by workflow, location, action, and permission scope.',
  },
  {
    title: 'Approvals protect key decisions',
    body: 'Route consequential or ambiguous work to the right person with the source context attached.',
  },
  {
    title: 'The activity trail stays complete',
    body: 'See the reason, action, result, and next state for every completed or escalated task.',
  },
];

export default function Alt4Home() {
  return (
    <Alt4Layout title="AI-native practice management system">
      <section className="alt4-home-hero alt4-section">
        <div className="alt4-container">
          <div className="alt4-home-hero__grid">
            <Reveal className="alt4-hero-copy">
              <p className="alt4-eyebrow">The AI-native PMS for dental groups</p>
              <h1>Your practice management system should run the practice.</h1>
              <p className="alt4-home-hero__lede">
                Asha brings your PMS, patient communications, revenue cycle, and office workflows
                into one operating system—so AI can complete work across every location, not just
                suggest the next step.
              </p>
              <div className="alt4-home-hero__actions">
                <PrimaryCta />
                <TextLink to="/alt4/autopilot" className="alt4-home-hero__secondary">
                  See how autopilot works <ArrowRight size={16} aria-hidden="true" />
                </TextLink>
              </div>
              <div className="alt4-home-hero__note">
                <ShieldCheck size={16} aria-hidden="true" />
                <span>Built for governed operations across emerging and large DSOs.</span>
              </div>
            </Reveal>
            <Reveal className="alt4-home-hero__signal" delay={0.1}>
              <div className="alt4-home-hero__signal-line" aria-hidden="true" />
              <div>
                <span>Operating model</span>
                <strong>One system that can see, decide, and act</strong>
              </div>
            </Reveal>
          </div>
          <Reveal className="alt4-home-hero__product" delay={0.16}>
            <PracticeCommandCenter />
          </Reveal>
        </div>
      </section>

      <section className="alt4-section alt4-home-handoffs">
        <div className="alt4-container">
          <SectionIntro
            eyebrow="From handoffs to continuity"
            title="When systems hand off work, context gets lost."
            body="Office work rarely fits inside one screen. A change in the schedule can affect outreach, visit readiness, clinical work, claims, and follow-up. An AI-native PMS keeps those steps connected to the same live context."
          />
          <div className="alt4-home-handoff-map" aria-label="One event flowing through a shared operating context">
            <div className="alt4-home-handoff-map__event">
              <span>Practice event</span>
              <strong>A schedule changes</strong>
            </div>
            <div className="alt4-home-handoff-map__path" aria-hidden="true">
              <i />
              <ArrowRight size={18} />
            </div>
            <div className="alt4-home-handoff-map__core">
              <Layers3 size={20} aria-hidden="true" />
              <span>Shared operating context</span>
              <strong>The next authorized work begins</strong>
            </div>
            <div className="alt4-home-handoff-map__path" aria-hidden="true">
              <i />
              <ArrowRight size={18} />
            </div>
            <div className="alt4-home-handoff-map__result">
              <StatusPill tone="green">Completed</StatusPill>
              <strong>Office and patient stay in sync</strong>
            </div>
          </div>
          <div className="alt4-grid alt4-grid--3 alt4-home-handoff-cards">
            {handoffCards.map((card) => (
              <article className="alt4-card" key={card.title}>
                <Check size={18} aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="alt4-section alt4-home-layer">
        <div className="alt4-container">
          <SectionIntro
            eyebrow="The operating layer"
            title="One system carries context into action."
            body="The practice core, communications, clinical work, and revenue operations share the same current record—giving Autopilot the context and action surfaces required to finish work."
          />
          <div className="alt4-home-layer__grid">
            {OPERATING_LAYERS.map((layer, index) => (
              <article className="alt4-home-layer__card" key={layer.title}>
                <span>{String(index + 1).padStart(2, '0')}</span>
                <div>
                  <h3>{layer.title}</h3>
                  <p>{layer.description}</p>
                  {layer.detail && <small>{layer.detail}</small>}
                </div>
              </article>
            ))}
          </div>
          <TextLink to="/alt4/platform" className="alt4-home-section-link">
            Explore the operating layer <ArrowRight size={16} aria-hidden="true" />
          </TextLink>
        </div>
      </section>

      <section className="alt4-section alt4-home-lifecycle">
        <div className="alt4-container">
          <SectionIntro
            eyebrow="One continuous patient journey"
            title="The patient journey stays connected."
            body="Each completed step updates what the PMS knows and what the office should do next. Work moves forward without becoming a new disconnected queue at every stage."
          />
          <ol className="alt4-home-lifecycle__rail">
            {lifecycleStages.map((stage, index) => (
              <li key={stage.label}>
                <div className="alt4-home-lifecycle__number">{index + 1}</div>
                <div>
                  <h3>{stage.label}</h3>
                  <p>
                    {stage.workflowIndex === undefined
                      ? stage.detail
                      : WORKFLOW_GROUPS[stage.workflowIndex]?.summary || stage.detail}
                  </p>
                </div>
                {index < lifecycleStages.length - 1 && <ArrowRight size={18} aria-hidden="true" />}
              </li>
            ))}
          </ol>
          <TextLink to="/alt4/workflows" className="alt4-home-section-link">
            See connected workflows <ArrowRight size={16} aria-hidden="true" />
          </TextLink>
        </div>
      </section>

      <section className="alt4-section alt4-home-dso">
        <div className="alt4-container alt4-home-dso__grid">
          <div>
            <SectionIntro
              eyebrow="The DSO control plane"
              title="One operating model. Every location in view."
              body="Central teams can set policy, permissions, and shared operating standards while each location keeps the configuration and exception handling its day-to-day work requires."
            />
            <TextLink to="/alt4/practices" className="alt4-home-section-link">
              Explore multi-location operations <ArrowRight size={16} aria-hidden="true" />
            </TextLink>
          </div>
          <div className="alt4-home-dso__map" aria-label="Organization-wide operations view">
            <div className="alt4-home-dso__map-header">
              <div>
                <Map size={18} aria-hidden="true" />
                <strong>All-location operations</strong>
              </div>
              <StatusPill tone="green">Live context</StatusPill>
            </div>
            {[
              ['Central policy', 'Applied to authorized workflows'],
              ['Local configuration', 'Adapted to office operations'],
              ['Permission scopes', 'Matched to roles and actions'],
              ['Exception queues', 'Routed with full context'],
            ].map(([title, detail]) => (
              <div className="alt4-home-dso__map-row" key={title}>
                <span><GitBranch size={15} aria-hidden="true" /></span>
                <div><strong>{title}</strong><small>{detail}</small></div>
                <Check size={15} aria-hidden="true" />
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="alt4-section alt4-panel--blue alt4-home-governance">
        <div className="alt4-container">
          <SectionIntro
            eyebrow="Governed autonomy"
            title="Autopilot moves the work. Your controls define how."
            body="Give the PMS room to complete routine work while preserving review, approval, and escalation wherever your operating model requires it."
            inverse
          />
          <div className="alt4-grid alt4-grid--3">
            {governanceCards.map((card) => (
              <article className="alt4-home-governance__card" key={card.title}>
                <ShieldCheck size={20} aria-hidden="true" />
                <h3>{card.title}</h3>
                <p>{card.body}</p>
              </article>
            ))}
          </div>
          <TextLink to="/alt4/autopilot" className="alt4-home-governance__link">
            See autonomy and controls <ArrowRight size={16} aria-hidden="true" />
          </TextLink>
        </div>
      </section>

      <section className="alt4-section alt4-home-migration">
        <div className="alt4-container">
          <SectionIntro
            eyebrow="A staged path forward"
            title="Move the operation, not just the records."
            body="Migration aligns data, configuration, workflows, and team readiness. Roll out by location, validate real operating scenarios, and keep tuning after launch."
          />
          <ol className="alt4-home-migration__steps">
            {MIGRATION_STEPS.map((step, index) => (
              <li key={step.title}>
                <span>{step.number || String(index + 1).padStart(2, '0')}</span>
                <h3>{step.title}</h3>
                <p>{step.description}</p>
              </li>
            ))}
          </ol>
          <TextLink to="/alt4/migration" className="alt4-home-section-link">
            Explore the migration approach <ArrowRight size={16} aria-hidden="true" />
          </TextLink>
        </div>
      </section>

      <ClosingCta
        eyebrow="See the operating system in action"
        title="See your practice run as one system."
        body="Walk through how an AI-native PMS can carry work across locations while your team stays in control."
      />
    </Alt4Layout>
  );
}
